import React from 'react';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight } from 'lucide-react';
import { LatexRenderer } from '../../ui/LatexRenderer';
interface AnswerComparisonProps {
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
}
/**
 * AnswerComparison — polished answer reveal with smooth entrance.
 * Correct: single emerald card with large checkmark.
 * Wrong: user answer (rose) + arrow + correct answer (emerald) side by side.
 */
export function AnswerComparison({
  userAnswer,
  correctAnswer,
  isCorrect,
  mode = 'full'
}: AnswerComparisonProps) {
  if (mode === 'compact') {
    return <div className="grid gap-2" data-id="element-4314">
        <div className={`p-3 rounded-xl border-2 ${isCorrect ? 'border-emerald-200 bg-emerald-50' : 'border-rose-100 bg-rose-50'}`} data-id="element-4315">
          <div className="text-[10px] font-black uppercase mb-1 opacity-60" data-id="element-4316">
            Jouw antwoord
          </div>
          <div className="font-bold text-slate-900 text-sm" data-id="element-4317">
            <LatexRenderer content={userAnswer} data-id="element-4318" />
          </div>
        </div>

        {!isCorrect && <div className="p-3 rounded-xl border-2 border-emerald-200 bg-emerald-50" data-id="element-4319">
            <div className="text-[10px] font-black uppercase mb-1 text-emerald-700" data-id="element-4320">
              Goede antwoord
            </div>
            <div className="font-bold text-emerald-900 text-sm" data-id="element-4321">
              <LatexRenderer content={correctAnswer} data-id="element-4322" />
            </div>
          </div>}
      </div>;
  }
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.97
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 shadow-sm" data-id="element-4323">
        <div className="flex items-center gap-4" data-id="element-4324">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.2, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.4,
          times: [0, 0.6, 1]
        }} className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-md flex-shrink-0" data-id="element-4325">
            <Check className="w-7 h-7 text-white" strokeWidth={3} data-id="element-4326" />
          </motion.div>
          <div data-id="element-4327">
            <div className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-1" data-id="element-4328">
              Jouw antwoord
            </div>
            <div className="font-black text-2xl text-emerald-900" data-id="element-4329">
              <LatexRenderer content={userAnswer} data-id="element-4330" />
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Wrong — show user answer and correct answer with arrow between
  return <div className="flex flex-col md:flex-row gap-3 items-stretch" data-id="element-4331">
      {/* User's wrong answer */}
      <motion.div initial={{
      opacity: 0,
      x: -16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-4332">
        <div className="bg-rose-50 rounded-3xl p-6 border-2 border-rose-200 h-full shadow-sm" data-id="element-4333">
          <div className="flex items-center gap-3 mb-3" data-id="element-4334">
            <div className="w-9 h-9 rounded-xl bg-rose-200 flex items-center justify-center text-rose-700 shadow-sm flex-shrink-0" data-id="element-4335">
              <X className="w-5 h-5" strokeWidth={3} data-id="element-4336" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-rose-600" data-id="element-4337">
              Jouw antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-rose-900 pl-12" data-id="element-4338">
            <LatexRenderer content={userAnswer} data-id="element-4339" />
          </div>
        </div>
      </motion.div>

      {/* Arrow separator */}
      <motion.div initial={{
      opacity: 0,
      scale: 0
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.15,
      duration: 0.3
    }} className="hidden md:flex items-center justify-center flex-shrink-0" data-id="element-4340">
        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-4341">
          <ArrowRight className="w-5 h-5 text-slate-400" strokeWidth={2.5} data-id="element-4342" />
        </div>
      </motion.div>

      {/* Correct answer */}
      <motion.div initial={{
      opacity: 0,
      x: 16
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} className="flex-1" data-id="element-4343">
        <div className="bg-emerald-50 rounded-3xl p-6 border-2 border-emerald-200 h-full shadow-sm" data-id="element-4344">
          <div className="flex items-center gap-3 mb-3" data-id="element-4345">
            <div className="w-9 h-9 rounded-xl bg-emerald-200 flex items-center justify-center text-emerald-700 shadow-sm flex-shrink-0" data-id="element-4346">
              <Check className="w-5 h-5" strokeWidth={3} data-id="element-4347" />
            </div>
            <span className="text-xs font-black uppercase tracking-wider text-emerald-600" data-id="element-4348">
              Het juiste antwoord
            </span>
          </div>
          <div className="font-black text-2xl text-emerald-900 pl-12" data-id="element-4349">
            <LatexRenderer content={correctAnswer} data-id="element-4350" />
          </div>
        </div>
      </motion.div>
    </div>;
}